import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import T from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { black, blackAlpha32, purple16, purple8, purpleAlpha56, purpleAlpha8, white } from '../../styles/colors';
import { getFocusRing, resetBrowserOutline, resetButtonStyle } from '../../styles/mixins';
import { body2 } from '../../styles/typography';
import { unit } from '../../styles/unit';
import { Icon } from '../Icon/Icon';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Container = /*#__PURE__*/styled.li.withConfig({
  displayName: "Container",
  componentId: "sc-1gwkf28-0"
})(["", ";", " display:flex;flex-direction:column;justify-content:center;height:40px;"], resetBrowserOutline, body2);
const StyledIcon = /*#__PURE__*/styled(Icon).withConfig({
  displayName: "StyledIcon",
  componentId: "sc-1gwkf28-1"
})(["margin-right:", ";"], unit(2));
const Thumb = /*#__PURE__*/styled.img.withConfig({
  displayName: "Thumb",
  componentId: "sc-1gwkf28-2"
})(["margin-right:", ";width:20px;max-height:20px;vertical-align:middle;"], unit(2));
const defaultItemStyle = /*#__PURE__*/css(["", ";justify-content:flex-start;padding:0 ", ";text-align:left;background:transparent;line-height:inherit;font-size:inherit;color:", ";letter-spacing:0.2px;background-color:", ";&:hover{background-color:", ";}&.focus-visible{box-shadow:inset ", ";}&[aria-current],&[aria-selected='true']{background-color:", ";}&:active{background-color:", ";}&[aria-disabled='true']{color:", ";&:hover,&:active{background-color:", ";}}"], resetBrowserOutline, unit(4), black, white, purple8, getFocusRing(purpleAlpha56), purpleAlpha8, purple16, blackAlpha32, white);
const LinkItem = /*#__PURE__*/styled.button.withConfig({
  displayName: "LinkItem",
  componentId: "sc-1gwkf28-3"
})(["", ";", ";width:100%;height:100%;"], resetButtonStyle, defaultItemStyle);
const TextItem = /*#__PURE__*/styled.span.withConfig({
  displayName: "TextItem",
  componentId: "sc-1gwkf28-4"
})(["", ";cursor:default;"], defaultItemStyle);
const MenuItem = /*#__PURE__*/forwardRef((_ref, forwardedRef) => {
  let {
    role = 'menuitem',
    as,
    href,
    onClick,
    selected = false,
    disabled = false,
    children,
    glyph,
    thumbnail,
    tabIndex = -1
  } = _ref,
      restProps = _objectWithoutPropertiesLoose(_ref, ["role", "as", "href", "onClick", "selected", "disabled", "children", "glyph", "thumbnail", "tabIndex"]);

  const ariaSelectedAttr = role === 'option' ? 'aria-selected' : 'aria-current';
  const itemProps = Object.assign({
    [ariaSelectedAttr]: selected ? 'true' : undefined
  }, restProps, {
    ref: forwardedRef,
    tabIndex,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [glyph && /*#__PURE__*/_jsx(StyledIcon, {
        glyph: glyph,
        size: "xsmall"
      }), thumbnail && /*#__PURE__*/_jsx(Thumb, {
        src: thumbnail
      }), children]
    })
  });
  const item = disabled ? /*#__PURE__*/_jsx(TextItem, Object.assign({
    role: role
  }, itemProps, {
    "aria-disabled": "true"
  })) : /*#__PURE__*/_jsx(LinkItem, Object.assign({
    as: as || (href ? 'a' : undefined),
    role: role,
    href: href,
    onClick: onClick
  }, itemProps));
  return /*#__PURE__*/_jsx(Container, {
    role: "none",
    children: item
  });
});
MenuItem.propTypes = {
  children: T.node.isRequired,
  as: T.elementType,
  href: T.string,
  onClick: T.func,
  glyph: T.elementType,
  selected: T.bool,
  disabled: T.bool,
  thumbnail: T.string,
  role: T.string,
  tabIndex: T.oneOfType([T.string, T.number])
};
export { MenuItem };